import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useHubMember } from "@/lib/HubMemberContext";
import { User, Mail, Phone, Lock, LogOut, Save, IdCard, ArrowLeft, ShieldCheck, MessageCircle, CheckCircle2 } from "lucide-react";

/**
 * Member profile page.
 * Shows Hub-ID, name, email (read-only — comes from Google), phone.
 * Lets the member set (or change) a password so they can log in with email/password next time.
 */
export default function MemberProfile() {
  const { ready, member, refresh, logout } = useHubMember();
  const navigate = useNavigate();
  const [hasPassword, setHasPassword] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!ready) return;
    if (!member) { navigate("/member/login", { replace: true }); return; }
    axios.get(`${API}/api/auth/hub/has-password`, { withCredentials: true })
      .then((r) => setHasPassword(!!r.data.has_password))
      .catch(() => {});
  }, [ready, member, navigate, API]);

  if (!ready) return <div className="min-h-[60vh] grid place-items-center text-white/40">Loading…</div>;
  if (!member) return null;

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setSavingPw(true);
    try {
      await axios.post(`${API}/api/auth/hub/set-password`, { password: pw }, { withCredentials: true });
      toast.success(hasPassword ? "Password changed" : "Password set — you can now log in with email + password");
      setPw(""); setPw2("");
      setHasPassword(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save password");
    } finally { setSavingPw(false); }
  };

  const signOut = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/", { replace: true });
  };

  return (
    <section className="max-w-3xl mx-auto py-10 md:py-14 px-5" data-testid="member-profile-page">
      <Link to="/me" className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white mb-6">
        <ArrowLeft size={12} /> Back to portal
      </Link>

      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Member · Profile</div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none mb-8">My Profile.</h1>

      {/* WhatsApp verification card — only when phone is not verified yet */}
      {!member.phone_verified && (
        <VerifyWhatsAppCard member={member} onVerified={refresh} />
      )}

      <div className="border border-white/12 bg-[#131A2B] p-6 mb-6">
        <div className="flex items-center gap-4 pb-4 border-b border-white/10">
          {member.picture ? (
            <img src={member.picture} alt="" className="w-16 h-16 rounded-full border-2 border-brand-orange" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-brand-blue/20 grid place-items-center brand-blue text-2xl font-black uppercase">
              {(member.name || "M").charAt(0)}
            </div>
          )}
          <div>
            <div className="font-display text-2xl font-black uppercase">{member.name}</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-white/50">Hub-ID · {member.hub_id}</div>
          </div>
        </div>

        <Field icon={IdCard}  label="Hub Member ID" value={member.hub_id} note="Your unique ID at HTP Sports Hub" />
        <Field icon={User}    label="Name" value={member.name} />
        <Field icon={Mail}    label="Email" value={member.email} note="Comes from your Google account · cannot be changed" />
        <Field icon={Phone}   label="WhatsApp phone" value={member.phone} note={member.phone_verified ? "Verified ✓" : "Not yet verified"} />
      </div>

      {/* Password */}
      <div className="border border-white/12 bg-[#131A2B] p-6 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="brand-blue" size={16} />
          <div className="font-display text-lg font-black uppercase">
            {hasPassword ? "Change Password" : "Set a Password"}
          </div>
        </div>
        <div className="text-xs text-white/50 mb-4">
          {hasPassword
            ? "Update the password you use for email/password sign-in."
            : "Set a password so next time you can sign in without Google — just email + password."}
        </div>
        <form onSubmit={savePassword} className="space-y-3">
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">New password</div>
            <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
              <Lock size={14} className="ml-3 text-white/40" />
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                data-testid="profile-new-password" autoComplete="new-password"
                placeholder="At least 6 characters"
                className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
            </div>
          </label>
          <label className="block">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Confirm password</div>
            <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
              <Lock size={14} className="ml-3 text-white/40" />
              <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                data-testid="profile-confirm-password" autoComplete="new-password"
                placeholder="Type again"
                className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
            </div>
          </label>
          <button type="submit" disabled={savingPw}
            data-testid="profile-save-password"
            className="w-full bg-brand-blue text-white py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white hover:text-black disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <Save size={14} /> {savingPw ? "Saving…" : (hasPassword ? "Update password" : "Set password")}
          </button>
        </form>
      </div>

      {/* Sign out */}
      <button onClick={signOut} data-testid="profile-signout"
        className="w-full border border-red-500/40 text-red-300 py-3 font-bold text-xs uppercase tracking-widest hover:bg-red-500/10 inline-flex items-center justify-center gap-2">
        <LogOut size={14} /> Sign out of this device
      </button>
    </section>
  );
}


function Field({ icon: Icon, label, value, note }) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-white/8 last:border-0">
      <div className="mt-0.5 text-white/40"><Icon size={16} /></div>
      <div className="flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
        <div className="text-sm mt-0.5">{value || <span className="text-white/40">—</span>}</div>
        {note && <div className="text-[10px] text-white/40 mt-0.5">{note}</div>}
      </div>
    </div>
  );
}

function VerifyWhatsAppCard({ member, onVerified }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const requestOtp = async (isResend = false) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a 10-digit mobile number"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/google/request-otp`, { phone: digits }, { withCredentials: true });
      setOtpSent(true);
      setCountdown(30);
      toast.success(isResend ? "OTP re-sent on WhatsApp" : `OTP sent on WhatsApp to +91 ${digits.slice(-10)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, "");
    const code = otp.replace(/\D/g, "");
    if (code.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/auth/google/set-phone`,
        { phone: digits, otp: code, marketing_opt_in: optIn },
        { withCredentials: true });
      toast.success(`Verified · Hub-ID ${r.data.hub_id}`);
      await onVerified();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "OTP verification failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-brand-orange bg-brand-orange/5 p-6 mb-6" data-testid="profile-verify-whatsapp">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-brand-orange/20 border border-brand-orange grid place-items-center shrink-0">
          <MessageCircle className="brand-orange" size={18} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange">// One more step</div>
          <div className="font-display text-xl md:text-2xl font-black uppercase mt-0.5">Verify your WhatsApp</div>
          <div className="text-xs text-white/60 mt-1">
            Hi <b className="brand-orange">{member.name}</b> — we&apos;ll send a 6-digit code to your WhatsApp so we can confirm the number is yours.
          </div>
        </div>
      </div>

      <form onSubmit={verify} className="space-y-3">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">WhatsApp mobile number</label>
          <div className="flex items-center gap-2 border border-white/20 bg-black/40 px-3 py-2 focus-within:border-brand-orange">
            <span className="text-white/50 text-sm">+91</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)}
              data-testid="profile-phone-input" placeholder="9876543210" inputMode="numeric" maxLength="12"
              disabled={otpSent}
              className="flex-1 bg-transparent outline-none text-sm disabled:opacity-60" />
            <Phone size={14} className="text-white/40" />
          </div>
        </div>

        {!otpSent && (
          <button type="button" onClick={() => requestOtp(false)} disabled={busy}
            data-testid="profile-send-otp"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-black py-3 font-bold text-xs uppercase tracking-widest disabled:opacity-50 hover:bg-white transition">
            <MessageCircle size={14} /> {busy ? "Sending…" : "Send OTP on WhatsApp"}
          </button>
        )}

        {otpSent && (
          <>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Enter 6-digit OTP</label>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} required
                data-testid="profile-otp-input" placeholder="123456" inputMode="numeric" maxLength="6"
                autoFocus
                className="w-full bg-black/40 border border-white/20 px-3 py-3 text-center text-lg font-mono tracking-[0.5em] focus:border-brand-orange outline-none" />
              <div className="mt-1 flex justify-between items-center text-[10px] font-mono text-white/40">
                <span>Sent to +91 {phone.replace(/\D/g, "").slice(-10)}</span>
                {countdown > 0 ? (
                  <span>Resend in {countdown}s</span>
                ) : (
                  <button type="button" onClick={() => requestOtp(true)} className="text-brand-orange hover:underline" data-testid="profile-resend-otp">Resend OTP</button>
                )}
              </div>
            </div>
            <label className="flex items-start gap-2 text-[11px] text-white/60">
              <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)}
                data-testid="profile-optin" className="mt-0.5 accent-brand-orange" />
              I&apos;m OK with receiving booking updates, membership renewals, and offers on WhatsApp.
            </label>
            <button type="submit" disabled={busy} data-testid="profile-verify-otp"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-orange text-black py-3 font-bold text-xs uppercase tracking-widest disabled:opacity-50 hover:bg-white transition">
              <CheckCircle2 size={14} /> {busy ? "Verifying…" : "Verify & Continue"}
            </button>
            <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }}
              data-testid="profile-change-number"
              className="w-full text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/70">
              Change number
            </button>
          </>
        )}
      </form>
    </div>
  );
}

